import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../../../../axiosInstance';
import { useTranslation } from 'react-i18next';
import Badge from 'apollo-react/components/Badge';
import io from 'socket.io-client';

import StyledTabs from '../../../common/StyledTabs/StyledTabs';
import ReviewsListCards from '../../../common/Reviews_Cards/list';
import { DraftsList } from '../Drafts';
import {
  REQUEST_STATUS,
  CUSTOMER_STATUS_ORDER,
  CUSTOMER_TAG_OPTIONS,
  TAB_STATE_KEYS,
} from '../../../../constants/request.constant';
import { useActiveTab } from '../../../../contexts/ActiveTabContext';
import useDraftsApi from '../../../../hooks/api/drafts/useDraftsApi';

const INITIAL_CUSTOMER_COUNT = {
  [REQUEST_STATUS.PENDING]: 0,
  [REQUEST_STATUS.IN_PROGRESS]: 0,
  [REQUEST_STATUS.APPROVED]: 0,
  [REQUEST_STATUS.REJECTED]: 0,
  [REQUEST_STATUS.DRAFT]: 0,
};

const MyRequests = () => {
  const { t } = useTranslation();
  const { activeTab, setActiveTab } = useActiveTab(
    TAB_STATE_KEYS.CUSTOMER_MY_REQUESTS
  );
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState(
    () => CUSTOMER_STATUS_ORDER[activeTab] || REQUEST_STATUS.PENDING
  );
  const [count, setCount] = useState(INITIAL_CUSTOMER_COUNT);
  const statusRef = useRef(status);
  const socketRef = useRef(null);
  const { getDraftCount } = useDraftsApi();

  // Fetch draft count
  const fetchDraftCount = useCallback(async () => {
    try {
      const draftCount = await getDraftCount();
      setCount((prev) => ({ ...prev, [REQUEST_STATUS.DRAFT]: draftCount }));
    } catch (err) {
      console.error('Error fetching draft count:', err);
    }
  }, [getDraftCount]);

  // keep statusRef in sync with status
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    setStatus(CUSTOMER_STATUS_ORDER[activeTab] || REQUEST_STATUS.PENDING);
  }, [activeTab]);

  useEffect(() => {
    if (status === REQUEST_STATUS.DRAFT) {
      return;
    }

    setRequests([]);

    const fetchMyRequests = async () => {
      try {
        const response = await axiosInstance.get('/api/requests/enduser', {
          params: { status },
        });
        setRequests(response?.data.requests || []);
      } catch (err) {
        console.error('Error fetching requests:', err);
        // Set empty array on error to prevent crash
        setRequests([]);
      }
    };

    const fetchMyRequestsCount = async () => {
      try {
        const response = await axiosInstance.get(
          // eslint-disable-next-line no-undef
          `${process.env.REACT_APP_SERVER_URL}/api/requests/enduser/count`
        );
        setCount((prev) => ({ ...prev, ...response?.data.count }));
      } catch (err) {
        console.error('Error fetching requests count:', err);
      }
    };
    fetchMyRequests();
    // get count on each tab.
    fetchMyRequestsCount();
    fetchDraftCount();
  }, [status, fetchDraftCount]);

  // socket connection persistent throughout component lifecycle
  useEffect(() => {
    // fetch current end user ID first
    axiosInstance
      .get(
        // eslint-disable-next-line no-undef
        `${process.env.REACT_APP_SERVER_URL}/api/users/me`
      )
      .then((response) => {
        const userId = response?.data?.user?.id;

        if (!userId) {
          console.error('[END USER] No user ID found');
          return;
        }

        // eslint-disable-next-line no-undef
        const socket = io(process.env.REACT_APP_SERVER_URL, {
          withCredentials: true,
          query: { userId },
        });

        socketRef.current = socket;

        const onChanged = (data) => {
          if (data?.scope === 'endUser') {
            console.log('[END USER] Scope is endUser - fetching data');

            // always refresh counts
            axiosInstance
              .get(
                // eslint-disable-next-line no-undef
                `${process.env.REACT_APP_SERVER_URL}/api/requests/enduser/count`
              )
              .then((response) => {
                console.log('[COUNT API] Success:', response?.data.count);
                setCount(response?.data.count);
              })
              .catch((error) => {
                console.error('[COUNT API] Failed:', error);
              });

            // refresh current list only - use statusRef.current
            axiosInstance
              .get('/api/requests/enduser', {
                params: { status: statusRef.current },
              })
              .then((response) => {
                console.log(
                  '[REQUESTS API] Success. Count:',
                  response?.data.requests?.length
                );
                setRequests(response?.data.requests || []);
              })
              .catch((error) => {
                console.error('[REQUESTS API] Failed:', error);
              });
          } else {
            console.log(
              '[END USER] Scope is NOT endUser, ignoring. Scope:',
              data?.scope
            );
          }
        };

        socket.on('requests:changed', onChanged);
      })
      .catch((error) => {
        console.error('[END USER] Failed to fetch user info:', error);
      });

    // cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const handleChangeTab = (event, value) => {
    const newStatus = CUSTOMER_STATUS_ORDER[value] || REQUEST_STATUS.PENDING;
    setStatus(newStatus);
    setActiveTab(value);
  };

  const tabConfigs = [
    {
      key: 'assigned',
      label: (
        <>
          {t('Assigned')}
          <Badge badgeContent={count[REQUEST_STATUS.PENDING]} />
        </>
      ),
    },
    {
      key: 'in_progress',
      label: (
        <>
          {t('In Progress')}
          <Badge badgeContent={count[REQUEST_STATUS.IN_PROGRESS]} />
        </>
      ),
    },
    {
      key: 'approved',
      label: (
        <>
          {t('Approved')}
          <Badge badgeContent={count[REQUEST_STATUS.APPROVED]} />
        </>
      ),
    },
    {
      key: 'rejected',
      label: (
        <>
          {t('Rejected')}
          <Badge badgeContent={count[REQUEST_STATUS.REJECTED]} />
        </>
      ),
    },
    {
      key: 'drafts',
      label: (
        <>
          {t('Drafts')}
          <Badge badgeContent={count[REQUEST_STATUS.DRAFT]} />
        </>
      ),
    },
  ];

  return (
    <div>
      <StyledTabs
        value={activeTab}
        onChange={handleChangeTab}
        tabs={tabConfigs}
      />

      {status === REQUEST_STATUS.DRAFT ? (
        <DraftsList onDraftDeleted={fetchDraftCount} />
      ) : (
        <ReviewsListCards
          requests={requests}
          activeTab={activeTab}
          tagOptions={CUSTOMER_TAG_OPTIONS[status]}
          status={status}
        />
      )}
    </div>
  );
};

export default MyRequests;
